import { get, isNil } from 'lodash'
import { i18n } from '@/plugins/i18n/i18n'

/**
 * 預約「設定已變更 / 已無關聯」欄位的共用呈現邏輯（CBGISSUE-243）
 *
 * 情境：預約成立後，店家把當時用到的服務人員 / 服務項目 / 子項目 / 設備 / 附加服務
 * 刪除或改綁定，之後再開「編輯預約」或「再次預約」時，這些欄位在畫面上會直接變空白、
 * 下拉只剩「無資料」，店家看不出原因。
 *
 * 共用做法（沿用 CBGISSUE-226 附加服務的呈現方式）：
 * 1. 失效的值不無聲清空，保留在欄位上並加註原因標記（灰色、不可重新選取）。
 * 2. 欄位下方補一行灰字說明原因與下一步。
 * 3. 下拉清單真的是空的時候，把「無資料」換成有原因的句子。
 * 4. 一次有兩個以上欄位失效時，最上方加一條摘要。
 */

// => 失效原因
export const staleReasonConfig = {
  removed: 'removed', // 已刪除
  unbound: 'unbound', // 已與此服務人員解除綁定
  unboundFromService: 'unboundFromService', // 已與此服務項目解除綁定
  typeChanged: 'typeChanged', // 服務項目的性質已被改過
  disabled: 'disabled', // 已停用
  outOfRange: 'outOfRange', // 已不在目前的可選範圍（時數）
}

// => 「不指定 / 全部」等非真實服務人員的 id，不需要做失效判斷
export const virtualUnitIds = ['all', 'notSpecify']

// => 標記在選項名稱後方的原因後綴，例如「洗髮（已刪除）」
export const staleSuffix = (reason) => (reason ? i18n.t(`reservationStaleField.suffix.${reason}`) : '')

export const staleOptionLabel = (name, reason) => `${name || ''}${staleSuffix(reason)}`

// => 欄位下方的原因說明
export const staleFieldHint = (field, reason, params = {}) => {
  if (!field || !reason) return ''
  return i18n.t(`reservationStaleField.hint.${field}.${reason}`, params)
}

// => 下拉清單為空時的原因說明
export const staleEmptyHint = (key, params = {}) => (key ? i18n.t(`reservationStaleField.empty.${key}`, params) : '')

// => 失效欄位阻擋送出時停在欄位上的訊息（詳細原因由欄位下方的 staleFieldHint 說明）
export const staleBlockedMessage = () => i18n.t('reservationStaleField.blocked')

// => 多個欄位同時失效時的摘要
export const staleSummaryText = (fieldLabels = []) => {
  if (!fieldLabels.length) return ''
  return i18n.t('reservationStaleField.summary.title', { fields: fieldLabels.join('、') })
}

const staleReasonKey = '__staleReason'

/**
 * 把「已被選取但已不在可選清單」的歷史值補回選項尾端。
 * 補回的是一份帶 __staleReason 的副本，僅供顯示與保留，選項本身會 disabled。
 */
export const withPreservedOption = (options, selected, reason, { idKey = 'id' } = {}) => {
  const list = [...(options || [])]
  if (!reason || !selected) return list
  const selectedId = get(selected, idKey)
  if (isNil(selectedId)) return list
  if (list.some((item) => get(item, idKey) === selectedId)) return list
  return [...list, { ...selected, [staleReasonKey]: reason }]
}

// => 直接把選項標記成失效（用於本身就沒有可選清單可比對的情境，例如附加服務快照）
export const markStaleOption = (option, reason) => ({ ...option, [staleReasonKey]: reason })

export const optionStaleReason = (option) => get(option, staleReasonKey) || null

export const isStaleOption = (option) => Boolean(optionStaleReason(option))
